"use client";

import React, { FC } from "react";

import Grid from "@mui/material/Grid";

import { BreadcrumbsItem } from "~/bff/types/BreadcrumbsItem";
import { Docs } from "~/bff/types/Docs";
import { ShoppingListProduct } from "~/bff/types/ShoppingListProduct";
import { SkuProductVariant } from "~/bff/types/SkuProductVariant";
import { Variants } from "~/bff/types/Variants";
import { WidgetInfo } from "~/bff/types/WidgetInfo";
import { ProductCard } from "~/components/product-card/component";
import { formTitleForId } from "~/helpers/form-title-for-id";
import { formatPriceToNumber } from "~/helpers/get-display-price";
import { UseStoreSelectorReturnTypes } from "~/hooks/store-selector/use-store-selector-data";
import { Nullable } from "~/types/general.types";

export interface ProductsGridItemProps {
  productData: Docs | ShoppingListProduct | null;
  isShoppingListLayout?: boolean;
  restorationRef?: {
    markerId: string | null;
    scrollRestorationRef: React.RefObject<
      (HTMLSpanElement & HTMLAnchorElement) | null
    >;
  };
  layout?: string;
  index: number;
  breadcrumbs?: BreadcrumbsItem[];
  pageNumberRef?: React.RefObject<number>;
  productToPageMap?: Map<string, number>;
  storeSelectorData?: UseStoreSelectorReturnTypes;
  widgetData?: Nullable<WidgetInfo>;
}

export const ProductGridItem: FC<ProductsGridItemProps> = (props) => {
  const {
    isShoppingListLayout,
    restorationRef,
    layout,
    index,
    breadcrumbs,
    pageNumberRef,
    productToPageMap,
    storeSelectorData,
    widgetData,
    productData,
  } = props;

  const isProductDataAbsent = !productData;
  const isProductDataIsDocs = !isProductDataAbsent && "brand" in productData;
  const isProductDataIsShoppingListProduct =
    !isProductDataAbsent && "addedAt" in productData;

  const productDataParsed = {
    title: (isProductDataIsDocs && productData?.title) || null,
    price: productData?.price || null,
    colorCount: (isProductDataIsDocs && productData?.colorCount) || null,
    thumb_image: (isProductDataIsDocs && productData?.thumb_image) || null,
    productCode:
      (isProductDataIsShoppingListProduct && productData?.productCode) || null,
    pid: (isProductDataIsDocs && productData?.pid) || null,
    url: (isProductDataIsDocs && productData?.url) || null,
    description: (isProductDataIsDocs && productData?.description) || null,
    sustainability: (isProductDataIsDocs && productData?.sustainability) || null,
    sku: (isProductDataIsShoppingListProduct && productData?.sku) || null,
    masterSkuId: (isProductDataIsDocs && productData?.masterSkuId) || null,
    variants: productData?.variants || [],
    sizeIsSelected:
      (isProductDataIsShoppingListProduct && productData?.sizeIsSelected) || false,
    brand: (isProductDataIsDocs && productData?.brand) || null,
    productSlug:
      (isProductDataIsShoppingListProduct && productData?.productSlug) || null,
    images: (isProductDataIsShoppingListProduct && productData?.images) || null,
    displayColor:
      (isProductDataIsShoppingListProduct && productData?.displayColor) || null,
    name: (isProductDataIsShoppingListProduct && productData?.name) || null,
    displaySize:
      (isProductDataIsShoppingListProduct && productData?.displaySize) || null,
    id: (isProductDataIsShoppingListProduct && productData?.id) || null,
    addedAt: (isProductDataIsShoppingListProduct && productData?.addedAt) || null,
    endDate: (isProductDataIsShoppingListProduct && productData?.endDate) || null,
    inventory:
      (isProductDataIsShoppingListProduct && productData?.inventory) || null,
    sku_color: (isProductDataIsDocs && productData?.sku_color) || null,
  };

  const {
    title,
    price,
    colorCount,
    thumb_image,
    pid,
    url,
    description,
    sustainability,
    sku,
    masterSkuId,
    variants,
    sizeIsSelected,
    brand,
    productSlug,
    displayColor,
    name,
    displaySize,
    id,
    addedAt,
    endDate,
    inventory,
    sku_color,
  } = productDataParsed;

  const shoppingListProductimage =
    (isProductDataIsShoppingListProduct && productData?.images?.[0]) || null;
  const variant =
    (isProductDataIsDocs && productData?.variants?.[0]?.sku_color) || null;
  const productId =
    (isProductDataIsDocs && productData?.pid) ||
    (isProductDataIsShoppingListProduct && productData?.productCode) ||
    null;

  const product = {
    ...productData,
    price: typeof price === "number" ? String(price) : price,
    addedAt,
    name: isShoppingListLayout ? name : title,
    description,
    rawPrice:
      (productData as ShoppingListProduct)?.rawPrice ??
      (productData as Docs)?.pricePrevious ??
      formatPriceToNumber((productData as Docs)?.price),
    rawClearancePrice:
      (productData as ShoppingListProduct)?.rawClearancePrice ??
      formatPriceToNumber((productData as Docs)?.price),
    masterPrice: typeof price === "number" ? String(price) : price,
    coloursNumber: colorCount ?? undefined,
    masterImage: {
      url: isShoppingListLayout ? shoppingListProductimage?.url : thumb_image,
      label: isShoppingListLayout ? shoppingListProductimage?.label : title,
    },
    productCode: productId,
    slug: isShoppingListLayout ? productSlug : url,
    sku: sku || masterSkuId,
    displayColor: displayColor || sku_color,
    displaySize,
    variants: variants?.map((variant) => ({
      ...variant,
      rawPrice:
        (variant as SkuProductVariant)?.rawPrice ??
        (variant as Variants)?.pricePrevious ??
        formatPriceToNumber((variant as Variants)?.sku_price),
      rawClearancePrice:
        (variant as SkuProductVariant)?.rawClearancePrice ??
        formatPriceToNumber((variant as Variants)?.sku_price),
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    })) as any[],
    sizeIsSelected,
    id,
    endDate,
    inventory,
    ...(sustainability || []).reduce(
      (result, itemKey) => ({
        ...result,
        [itemKey || ""]: true,
      }),
      {},
    ),
    categoryPathsDisplayable:
      (isProductDataIsDocs ? productData?.categoryPathsDisplayable : undefined) ??
      undefined,
  };

  const titleForId = formTitleForId(title);

  return (
    <Grid
      id={`product-card-${titleForId}-${pid}`}
      item
      xs={isShoppingListLayout ? 12 : 6}
      sm={4}
      md={4}
      lg={4}
      xl={3}
    >
      <ProductCard
        key={pid}
        id={pid}
        product={product}
        sizeIsSelected={sizeIsSelected}
        layout={layout}
        position={index + 1}
        brand={brand}
        variant={variant}
        breadcrumbs={breadcrumbs}
        storeSelectorData={storeSelectorData}
        productToPageMap={productToPageMap}
        restorationRef={
          restorationRef?.markerId === pid
            ? restorationRef?.scrollRestorationRef
            : null
        }
        pageNumberRef={pageNumberRef}
        widgetData={widgetData}
      />
    </Grid>
  );
};
